/**
 * ┌───────────────────────────────────────────────────────────────────────────┐
 * │ PKCE, for the Google sign-in — item 2 / D4.                               │
 * └───────────────────────────────────────────────────────────────────────────┘
 *
 * ⚠️⚠️ **THE WHOLE REASON THE BROWSER DOES ANY OF THIS IS `hard-won.md` WP8i-1.** GoTrue's default
 * OAuth return hands the browser `#access_token=…&refresh_token=…` in the URL fragment — and a
 * browser-held refresh token makes per-session revocation **unenforceable by construction**,
 * because the browser renews directly against GoTrue and our registry is never consulted. With
 * PKCE the browser is handed a `?code=` it cannot spend alone; the API exchanges it, registers the
 * session and sets the httpOnly cookie, exactly as a password sign-in does.
 *
 * ⚠️ **The verifier is not a secret we keep — it is a nonce that binds the exchange to the
 * authorize request that started it.** So an intercepted code is worth nothing without the tab
 * that started the flow, which is the property PKCE exists to provide.
 */

/** RFC 7636 allows 43–128 characters; 32 random bytes is 43 base64url characters, the floor. */
const VERIFIER_BYTES = 32;

/**
 * ⚠️ **`sessionStorage`, not `localStorage`.** The verifier is meaningful for one round trip in one
 * tab: `localStorage` would leave it readable to every other tab on the origin and outlive the
 * flow, and a second sign-in started elsewhere would overwrite the one in flight.
 */
export const VERIFIER_KEY = 'forecastify.oauth.verifier';

function base64url(bytes: Uint8Array): string {
  /* ⚠️ `String.fromCharCode` over the array rather than `TextDecoder`: these are raw bytes, not
     UTF-8, and decoding them as text mangles everything above 0x7f. */
  let binary = '';
  for (const byte of bytes) binary += String.fromCharCode(byte);
  return btoa(binary).replace(/\+/gu, '-').replace(/\//gu, '_').replace(/=+$/u, '');
}

/** A fresh verifier. Kept by the tab that made it, and sent to our API with the code. */
export function createVerifier(): string {
  return base64url(crypto.getRandomValues(new Uint8Array(VERIFIER_BYTES)));
}

/**
 * The `s256` challenge for a verifier.
 *
 * ⚠️ **`crypto.subtle` is undefined on an insecure origin**, which is every way somebody reaches
 * this app over a LAN address rather than `localhost`. The caller has to handle a rejection — the
 * honest outcome there is that the Google button does not work, not that it silently falls back to
 * a `plain` challenge, which would remove the binding the whole exchange rests on.
 */
export async function challengeFor(verifier: string): Promise<string> {
  const digest = await crypto.subtle.digest('SHA-256', new TextEncoder().encode(verifier));
  return base64url(new Uint8Array(digest));
}

/** Remember it for the redirect, and hand back the challenge to send with the authorize request. */
export async function beginPkce(): Promise<string> {
  const verifier = createVerifier();
  sessionStorage.setItem(VERIFIER_KEY, verifier);
  return challengeFor(verifier);
}

/**
 * The verifier this tab stored, consumed.
 *
 * ⚠️ **Removed as it is read, so a back button cannot replay the exchange.** The code is
 * single-use at GoTrue anyway; this is the half we control, and it keeps a spent verifier out of
 * storage for the rest of the browsing session.
 */
export function takeVerifier(): string | null {
  const verifier = sessionStorage.getItem(VERIFIER_KEY);
  sessionStorage.removeItem(VERIFIER_KEY);
  return verifier;
}

/**
 * 22 Oct 2026 · ⚠️ **A CONNECT comes back to the same callback as a sign-in** — one redirect URL
 * registered with GoTrue rather than two. What tells them apart is this: the address to return to,
 * set only by *Fiókom → Integrációk*'s Connect and spent by the callback.
 */
export const LINK_RETURN_KEY = 'forecastify.oauth.linkReturn';

export function beginLink(returnTo: string): void {
  sessionStorage.setItem(LINK_RETURN_KEY, returnTo);
}

export function takeLinkReturn(): string | null {
  const returnTo = sessionStorage.getItem(LINK_RETURN_KEY);
  sessionStorage.removeItem(LINK_RETURN_KEY);
  return returnTo;
}
